import { Injectable, OnDestroy, OnInit } from '@angular/core';
import {
  AuthorizationControllerService,
  UserControllerService,
  WorkCenterControllerService,
} from '@app/generated/service';
import { WorkCenter } from './work-center';
import { Project } from './project';
import { Site } from './site';
import { UserService } from '@app/core/services/user.service';
import { Observable, BehaviorSubject, Subject, lastValueFrom } from 'rxjs';
import { CompanyDto, ProjectDto, WorkCenterDto } from '@app/generated/model';
import { takeUntil } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ProfileService implements OnDestroy {
  private workCenters: BehaviorSubject<WorkCenter[]> = new BehaviorSubject<WorkCenter[]>([]);
  private projects: BehaviorSubject<Project[]> = new BehaviorSubject<Project[]>([]);
  private sites: BehaviorSubject<Site[]> = new BehaviorSubject<Site[]>([]);
  private workCenterSelectionChanged = false;
  private siteSelectionChanged = false;

  private destroy$: Subject<void> = new Subject();

  constructor(
    private userService: UserService,
    private authorizationService: AuthorizationControllerService,
    private workCenterService: WorkCenterControllerService,
    private userControllerService: UserControllerService,
  ) {}

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async load(): Promise<void> {
    const user = this.userService.get();

    this.workCenterService.getSites1().subscribe((s) =>
      this.sites.next(
        s.map((site) => {
          return Site.fromDto(site);
        }),
      ),
    );
    const workCenters = await this.workCenterService.getWorkCenters().toPromise();

    // Guard against user.projects being undefined or empty
    const userProjects = user.projects ?? [];
    this.projects.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.setSelectedProjects([...userProjects]);
    });

    this.workCenters.next(workCenters.map((w) => WorkCenter.fromDto(w)));
    // Guard against user.workCenters being undefined or empty
    const userWorkCenters = user.workCenters ?? [];
    await this.setSelectedWorkCenters([...userWorkCenters]);
  }

  getSites(): Observable<Site[]> {
    return this.sites;
  }

  getWorkCenters(): Observable<WorkCenter[]> {
    return this.workCenters;
  }

  getProjects(): Observable<Project[]> {
    return this.projects;
  }

  toggleWorkCenter(workCenterId: string) {
    this.workCenterSelectionChanged = true;
    this.workCenters.value.forEach((w) => {
      if (w.id === workCenterId) {
        w.selected = !w.selected;
      }
    });
  }

  toggleSite(siteId: string) {
    this.siteSelectionChanged = true;
    this.sites.value.forEach((s) => {
      if (s.id === siteId) {
        s.selected = !s.selected;
      }
    });
  }

  toggleProject(projectId: string) {
    this.projects.value.forEach((p) => {
      if (p.id === projectId) {
        p.selected = !p.selected;
      }
    });
  }

  getProjectCompanies(projectId: string): Observable<CompanyDto[]> {
    return this.userControllerService.getProjectCompanies(projectId);
  }

  async setSelectedCompany(projectId: string, companyId: string): Promise<void> {
    await lastValueFrom(this.userControllerService.setSelectedCompany(projectId, { companyId }));
  }

  async getWorkcentersForSite() {
    const selectedSites = this.sites.value.filter((s) => s.selected).map((s) => s.id);
    const previouslySelectedIds = new Set(this.workCenters.value.filter((w) => w.selected).map((w) => w.id));
    this.workCenterService.getWorkCentersForSites(selectedSites).subscribe((workcenters) => {
      this.workCenters.next(
        Array.from(workcenters).map((w) => {
          const workCenter = WorkCenter.fromDto(w);
          workCenter.selected = previouslySelectedIds.has(workCenter.id);
          return workCenter;
        }),
      );
    });
  }

  async getProjectsForWorkCenter() {
    if (!this.workCenterSelectionChanged) {
      return;
    }
    const selectedWorkCenters = this.workCenters.value.filter((w) => w.selected).map((w) => w.id);
    this.workCenterService.getProjectsForWorkCenters(selectedWorkCenters).subscribe((projects) => {
      this.projects.next(Array.from(projects).map((p) => Project.fromDto(p)));
    });
    this.workCenterSelectionChanged = false;
  }

  public setAuthorizationProfile(authorization: any): Observable<any> {
    return this.authorizationService.activateAuthorization(authorization);
  }

  private async setSelectedWorkCenters(workcenters: Array<WorkCenterDto>): Promise<void> {
    if (!workcenters.length) {
      this.workCenters.value.forEach((w) => (w.selected = false));
      this.projects.next([]);
      return;
    }
    this.workCenters.value.forEach((w) => (w.selected = workcenters.some((wc) => wc.id === w.id)));
    this.workCenterSelectionChanged = true;
    await this.getProjectsForWorkCenter();
  }

  private setSelectedProjects(projects: Array<ProjectDto>): Promise<void> {
    if (!projects.length) {
      this.projects.value.forEach((p) => (p.selected = false));
      return;
    }

    this.projects.value.forEach(
      (p) =>
        (p.selected = projects.some((pj) => {
          return p.id === pj.id;
        })),
    );
  }

  private async setProfile(siteIds: Array<string>, workCenterIds: Array<string>, projectIds: Array<string>) {
    await this.userService.setProfile(siteIds, workCenterIds, projectIds);
    await this.userService.load();
  }
}
